<script setup lang="ts">
/**
 * A sample CV written out as text, next to its rendered sheet on
 * /resume-examples/<slug>.
 *
 * The sheet is an iframe, which neither search engines nor screen readers
 * treat as part of the page, and a visitor who wants to borrow a line should be
 * able to select it. Same markup as the finished example on
 * /how-to-write-a-resume, read straight from the CV data so the two can never
 * say different things.
 */
import type { CVData } from '~/types/cv'

const props = defineProps<{ cv: CVData }>()

const { t, locale } = useI18n()

/** YYYY-MM → "Aug 2021"; anything else is shown as written. */
function formatDate(value?: string): string {
  const match = (value ?? '').match(/^(\d{4})-(\d{2})$/)
  if (!match) return value ?? ''
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
    .toLocaleString(({ cs: 'cs-CZ', de: 'de-DE', pl: 'pl-PL' } as Record<string, string>)[locale.value] ?? 'en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

function period(start?: string, end?: string): string {
  const from = formatDate(start)
  const to = end ? formatDate(end) : t('resumeExamples.text.present')
  return from ? `${from} – ${to}` : to
}

/** One achievement per line, the bullet glyph stripped, as the PDF templates do. */
function bullets(description: string): string[] {
  return description.split(/\r?\n/).map(line => line.replace(/^\s*[-–—•·*]\s+/, '').trim()).filter(Boolean)
}

/** Each detail kept whole on a narrow screen; a phone number broken at its dash reads as two. */
const contactParts = computed(() =>
  [props.cv.contact?.location, props.cv.contact?.email, props.cv.contact?.phone, props.cv.contact?.linkedin]
    .filter((part): part is string => Boolean(part)),
)

const skills = computed(() => [...(props.cv.technicalSkills ?? []), ...(props.cv.softSkills ?? [])].join(', '))
const languages = computed(() => (props.cv.languages ?? []).map(lang => `${lang.name} (${lang.level})`).join(', '))
</script>

<template>
  <article class="rounded-[0.625rem] border border-line bg-white p-6 sm:p-10 dark:border-gray-700 dark:bg-gray-900">
    <div class="display-heading display-heading-3">{{ cv.name }}</div>
    <div class="mt-1 text-lg font-semibold text-accent">{{ cv.title }}</div>
    <p class="mt-3 text-base text-muted dark:text-gray-400">
      <template v-for="(part, i) in contactParts" :key="part">
        <span v-if="i" aria-hidden="true">  ·  </span><span class="whitespace-nowrap">{{ part }}</span>
      </template>
    </p>

    <template v-if="cv.aboutMe">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeExamples.text.summary') }}</div>
      <p class="mt-3 text-base">{{ cv.aboutMe }}</p>
    </template>

    <template v-if="cv.experiences.length">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeExamples.text.experience') }}</div>
      <div v-for="(job, index) in cv.experiences" :key="index" class="mt-5">
        <div class="flex flex-wrap items-baseline justify-between gap-2">
          <span class="font-title font-bold text-ink dark:text-white">{{ job.title }} — {{ job.company }}</span>
          <span class="!text-sm text-muted dark:text-gray-400">{{ period(job.startDate, job.endDate) }}</span>
        </div>
        <ul class="mt-2 flex flex-col gap-1">
          <li v-for="(point, i) in bullets(job.description)" :key="i" class="flex items-start gap-2 text-base">
            <span aria-hidden="true">•</span>
            <span>{{ point }}</span>
          </li>
        </ul>
      </div>
    </template>

    <template v-for="section in cv.customSections ?? []" :key="section.sectionTitle">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ section.sectionTitle }}</div>
      <div v-for="(item, i) in section.items" :key="i" class="mt-4">
        <div class="font-title font-bold text-ink dark:text-white">{{ item.title }}</div>
        <p class="mt-1 text-base">{{ item.text }}</p>
      </div>
    </template>

    <template v-if="cv.education.length">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeExamples.text.education') }}</div>
      <div v-for="(school, index) in cv.education" :key="index" class="mt-3 flex flex-wrap items-baseline justify-between gap-2">
        <span class="text-base"><span class="font-semibold text-ink dark:text-white">{{ school.degree }}</span>, {{ school.school }}</span>
        <span class="!text-sm text-muted dark:text-gray-400">{{ period(school.startDate, school.endDate) }}</span>
      </div>
    </template>

    <template v-if="cv.certificates?.length">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeExamples.text.certificates') }}</div>
      <ul class="mt-3 flex flex-col gap-1">
        <li v-for="(cert, index) in cv.certificates" :key="index" class="text-base">
          <span class="font-semibold text-ink dark:text-white">{{ cert.name }}</span>, {{ cert.issuer }}<template v-if="cert.date">, {{ formatDate(cert.date) }}</template>
        </li>
      </ul>
    </template>

    <template v-if="skills">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeExamples.text.skills') }}</div>
      <p class="mt-3 text-base">{{ skills }}</p>
    </template>

    <template v-if="languages">
      <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeExamples.text.languages') }}</div>
      <p class="mt-3 text-base">{{ languages }}</p>
    </template>
  </article>
</template>
